// The athlete search filter catalog: the cascading stat menu, quick presets and small helpers.
// Shared by the coach Search page and the admin/partner filter panel (components/search/).
import { SPORT_POSITIONS, DEFAULT_SPORT, SPORTS } from '../constants/positions';

export const POSITIONS = SPORT_POSITIONS[DEFAULT_SPORT];

export const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

// year_offset is canonical: 0=Freshman … 3=Senior (matches iOS, the source of truth).
export const YEAR_LABELS = ['Freshman', 'Sophomore', 'Junior', 'Senior'];

// Grad-year filter window: two classes that have already graduated +
// current class + the next six. Recomputed on module load each refresh.
const _CURR_YEAR = new Date().getFullYear();
export const GRAD_YEAR_OPTIONS = Array.from({ length: 9 }, (_, i) => _CURR_YEAR - 2 + i)
  .map(y => ({ value: y, label: String(y) }));

// Cascading menu: Category → Stat (each stat supports min/max range)
export const STAT_MENU = [
  { group: 'Profile', stats: [
    { id: 'graduation_year', label: 'Grad Year', type: 'select', options: GRAD_YEAR_OPTIONS, filterKey: 'graduation_year' },
    { id: 'sport', label: 'Sport', type: 'select', options: SPORTS.map(s => ({ value: s, label: s })), filterKey: 'sport' },
    { id: 'position', label: 'Position', type: 'select', options: POSITIONS.map(p => ({ value: p, label: p })), filterKey: 'position' },
    { id: 'state', label: 'State', type: 'select', options: US_STATES.map(s => ({ value: s, label: s })), filterKey: 'state' },
    { id: 'power_score', label: 'Power Score', type: 'range', minKey: 'power_score_min', maxKey: 'power_score_max', unit: '' },
  ]},
  { group: 'Body', stats: [
    { id: 'height', label: 'Height (in)', type: 'range', minKey: 'height_min', maxKey: 'height_max', unit: 'in' },
    { id: 'weight', label: 'Weight (lbs)', type: 'range', minKey: 'weight_min', maxKey: 'weight_max', unit: 'lbs' },
    { id: 'wingspan', label: 'Wingspan (in)', type: 'range', minKey: 'wingspan_min', maxKey: 'wingspan_max', unit: 'in' },
    { id: 'hand_size', label: 'Hand Size (in)', type: 'range', step: '0.25', minKey: 'hand_size_min', maxKey: 'hand_size_max', unit: 'in' },
  ]},
  { group: 'Speed', stats: [
    { id: 'dash_40', label: '40yd Dash (s)', type: 'range', step: '0.01', minKey: 'dash_40_min', maxKey: 'dash_40_max', unit: 's' },
    { id: 'run_10', label: '10yd Split (s)', type: 'range', step: '0.01', minKey: 'run_10_min', maxKey: 'run_10_max', unit: 's' },
    { id: 'run_20', label: 'Flying 10 (s)', type: 'range', step: '0.01', minKey: 'run_20_min', maxKey: 'run_20_max', unit: 's' },
  ]},
  { group: 'Agility', stats: [
    { id: 'short_shuttle', label: 'Short Shuttle (s)', type: 'range', step: '0.01', minKey: 'short_shuttle_min', maxKey: 'short_shuttle_max', unit: 's' },
    { id: 'three_cone', label: '3-Cone Drill (s)', type: 'range', step: '0.01', minKey: 'three_cone_min', maxKey: 'three_cone_max', unit: 's' },
  ]},
  { group: 'Athleticism', stats: [
    { id: 'vertical_jump', label: 'Vertical Jump (in)', type: 'range', minKey: 'vertical_jump_min', maxKey: 'vertical_jump_max', unit: 'in' },
  ]},
  { group: 'Academics', stats: [
    { id: 'gpa_weighted', label: 'Weighted GPA', type: 'range', step: '0.1', minKey: 'gpa_weighted_min', maxKey: 'gpa_weighted_max', unit: '' },
    { id: 'gpa_unweighted', label: 'Unweighted GPA', type: 'range', step: '0.1', minKey: 'gpa_unweighted_min', maxKey: 'gpa_unweighted_max', unit: '' },
    { id: 'act', label: 'ACT', type: 'range', minKey: 'act_min', maxKey: 'act_max', unit: '' },
    { id: 'sat', label: 'SAT', type: 'range', step: '10', minKey: 'sat_min', maxKey: 'sat_max', unit: '' },
  ]},
  { group: 'Area Income', stats: [
    { id: 'income', label: 'Area Income ($)', type: 'range', step: '1000', minKey: 'income_min', maxKey: 'income_max', unit: '$' },
  ]},
  { group: 'Mental Skills', stats: [
    { id: 'coachability', label: 'Coachability score', type: 'select', filterKey: 'min_coachability', options: [
      { value: '6',  label: '6+' },
      { value: '8',  label: 'High 8+' },
      { value: '10', label: 'Elite 10+' },
    ]},
  ]},
  { group: 'Mental Toughness', stats: [
    { id: 'smtq', label: 'Mental toughness', type: 'select', filterKey: 'smtq_band_min', options: [
      { value: 'strong',      label: 'Solid or better' },
      { value: 'exceptional', label: 'Standout' },
    ]},
  ]},
  { group: 'Preferences', stats: [
    { id: 'hobby', label: 'Hobby', type: 'select', filterKey: 'hobby', options: [
      { value: 'gaming',              label: 'Gaming' },
      { value: 'music',               label: 'Music' },
      { value: 'social_media',        label: 'Social media' },
      { value: 'fitness_gym',         label: 'Fitness' },
      { value: 'reading_audiobooks',  label: 'Reading' },
      { value: 'watching_sports',     label: 'Watching sports' },
      { value: 'photography_video',   label: 'Photography' },
      { value: 'cooking_food',        label: 'Cooking' },
      { value: 'outdoor_activities',  label: 'Outdoor activities' },
      { value: 'collecting_building', label: 'Collecting' },
    ]},
    { id: 'college_region', label: 'Open to region', type: 'select', filterKey: 'college_region', options: [
      { value: 'west',      label: 'West' },
      { value: 'midwest',   label: 'Midwest' },
      { value: 'northeast', label: 'Northeast' },
      { value: 'southwest', label: 'Southwest' },
      { value: 'southeast', label: 'Southeast' },
    ]},
  ]},
  { group: 'College fit', stats: [
    { id: 'division', label: 'Division', type: 'select', filterKey: 'division', options: [
      { value: 'd1',   label: 'D1' },
      { value: 'd2',   label: 'D2' },
      { value: 'd3',   label: 'D3' },
      { value: 'naia', label: 'NAIA' },
      { value: 'juco', label: 'JUCO' },
    ]},
    { id: 'campus_setting', label: 'Campus setting', type: 'select', filterKey: 'campus_setting', options: [
      { value: 'urban',    label: 'Urban' },
      { value: 'suburban', label: 'Suburban' },
      { value: 'rural',    label: 'Rural' },
    ]},
    { id: 'school_size', label: 'School size', type: 'select', filterKey: 'school_size', options: [
      { value: 'small',  label: 'Small (<5k)' },
      { value: 'medium', label: 'Medium (5–15k)' },
      { value: 'large',  label: 'Large (15k+)' },
    ]},
    { id: 'school_type', label: 'Public or private', type: 'select', filterKey: 'school_type', options: [
      { value: 'public',  label: 'Public' },
      { value: 'private', label: 'Private' },
    ]},
  ]},
];

// Preset values mirror ios FilterCatalog.swift — keep the two in sync.
// key: writes minKey (≥, higher is better) or maxKey (≤, lower is better).
export const STAT_PRESETS = {
  power_score:   { key: 'min', values: [70, 80, 90],          fmt: (v) => `≥ ${v}` },
  height:        { key: 'min', values: [72, 74, 76],          fmt: (v) => `≥ ${Math.floor(v / 12)}'${v % 12}"` },
  weight:        { key: 'min', values: [180, 200, 220],       fmt: (v) => `≥ ${v} lb` },
  wingspan:      { key: 'min', values: [72, 76, 80],          fmt: (v) => `≥ ${v}"` },
  hand_size:     { key: 'min', values: [9.0, 9.5, 10.0],      fmt: (v) => `≥ ${v}"` },
  dash_40:       { key: 'max', values: [4.5, 4.6, 4.8],       fmt: (v) => `≤ ${v.toFixed(2)}` },
  run_10:        { key: 'max', values: [1.55, 1.65, 1.75],    fmt: (v) => `≤ ${v.toFixed(2)}` },
  run_20:        { key: 'max', values: [2.70, 2.85, 3.00],    fmt: (v) => `≤ ${v.toFixed(2)}` },
  short_shuttle: { key: 'max', values: [4.2, 4.4, 4.6],       fmt: (v) => `≤ ${v.toFixed(2)}` },
  three_cone:    { key: 'max', values: [6.9, 7.1, 7.3],       fmt: (v) => `≤ ${v.toFixed(2)}` },
  vertical_jump: { key: 'min', values: [30, 34, 38],          fmt: (v) => `≥ ${v}"` },
  gpa_weighted:  { key: 'min', values: [3.0, 3.5, 3.8],       fmt: (v) => `≥ ${v.toFixed(1)}` },
  gpa_unweighted:{ key: 'min', values: [3.0, 3.5, 3.8],       fmt: (v) => `≥ ${v.toFixed(1)}` },
  act:           { key: 'min', values: [20, 25, 30],          fmt: (v) => `≥ ${v}` },
  sat:           { key: 'min', values: [1000, 1200, 1400],    fmt: (v) => `≥ ${v}` },
};

// Given a filters object, return the stat IDs whose values are non-default —
// used to re-hydrate the visible chip row when a saved search is applied.
export const deriveActiveStatIds = (f) => {
  const ids = [];
  for (const g of STAT_MENU) {
    for (const s of g.stats) {
      if (s.type === 'range') {
        if (f[s.minKey] || f[s.maxKey]) ids.push(s.id);
      } else if (f[s.filterKey]) {
        ids.push(s.id);
      }
    }
  }
  return ids;
};


export function formatHeight(inches) {
  if (!inches) return 'N/A';
  const total = Math.round(inches); // avoid float-modulo artifacts on fractional heights
  return `${Math.floor(total / 12)}'${total % 12}"`;
}
